import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLinkIcon, UserRoundIcon } from 'lucide-react';
import type { Project, ResolvedBrand, ToolCategory } from '../types/project';
import { BrandLogo } from './BrandLogo';
import { ToolCard } from './ToolCard';
import { ScreenshotCard } from './ScreenshotCard';
import { DocumentRow } from './DocumentRow';
interface ProjectWorkspaceProps {
  project: Project;
  brand: ResolvedBrand;
}
export function ProjectWorkspace({
  project,
  brand
}: ProjectWorkspaceProps) {
  const categories = useMemo(() => {
    const unique: ToolCategory[] = [];
    project.tools.forEach(tool => {
      if (!unique.includes(tool.category)) unique.push(tool.category);
    });
    return unique;
  }, [project]);
  const [activeCategory, setActiveCategory] = useState<ToolCategory | 'All'>('All');
  const visibleTools = activeCategory === 'All' ? project.tools : project.tools.filter(tool => tool.category === activeCategory);
  const filters: (ToolCategory | 'All')[] = ['All', ...categories];
  const headingStyle: React.CSSProperties = {
    fontFamily: 'var(--b-display)',
    color: 'var(--b-text)',
    textTransform: brand.displayUppercase ? 'uppercase' : 'none',
    letterSpacing: brand.displayUppercase ? '0.02em' : '-0.01em'
  };
  return <motion.main key={project.id} initial={{
    opacity: 0,
    y: 14
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    ease: [0.22, 1, 0.36, 1]
  }} className="mx-auto w-full max-w-6xl px-6 pb-24 pt-12" style={{
    fontFamily: 'var(--b-body)'
  }} data-id="element-126" data-mp="3edowm3ht1rwg">
      <div className="max-w-3xl" data-id="element-127" data-mp="3s3si2s8v1hku">
        <BrandLogo brand={brand} size="lg" data-id="element-128" data-mp="3s4u1pl3xzce6" />

        <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.24em]" style={{
        color: 'var(--b-accent)'
      }} data-id="element-129" data-mp="yajvlzoum6d">
          {project.stage} · Updated {project.updated}
        </p>
        <h1 className="mt-3 text-4xl font-bold leading-tight sm:text-5xl" style={headingStyle} data-id="element-130" data-mp="av5i03s1ukk4">
          {project.name}
        </h1>
        <p className="mt-2 text-sm font-semibold" style={{
        color: 'var(--b-accent)'
      }} data-id="element-131" data-mp="3hdp82ahbcwqy">
          {project.client}
        </p>
        <p className="mt-4 text-sm leading-relaxed text-[var(--b-dim)]" data-id="element-132" data-mp="3cc9jw9juaa4c">{project.summary}</p>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-id="element-133" data-mp="3r4tkp3jio5im">
          <span style={{
          borderRadius: 'var(--b-radius)'
        }} className="inline-flex items-center gap-2 border border-[var(--b-border)] bg-[var(--b-surface)] px-3 py-1.5 text-xs text-[var(--b-dim)]" data-id="element-134" data-mp="3mbicoz2yxip4">
            <UserRoundIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-135" data-mp="3v6y9noz0sdyx" />
            Lead: {project.owner}
          </span>
          <a href={brand.site} target="_blank" rel="noreferrer noopener" style={{
          borderRadius: 'var(--b-radius)'
        }} className="inline-flex items-center gap-2 border border-[var(--b-border)] bg-[var(--b-surface)] px-3 py-1.5 text-xs font-medium text-[var(--b-text)] transition hover:border-[var(--b-accent)] hover:text-[var(--b-accent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--b-accent)]" data-id="element-136" data-mp="8xncpy7au5w2">
            <ExternalLinkIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-137" data-mp="ggwjrksc1shy" />
            {brand.site.replace('https://', '')}
          </a>
        </div>
      </div>

      <section aria-labelledby="tools-heading" className="mt-14" data-id="element-138" data-mp="leespb6co7py">
        <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-139" data-mp="7fynxps1srag">
          <div data-id="element-140" data-mp="bhjzn7suizj2">
            <h2 id="tools-heading" className="text-2xl font-bold" style={headingStyle} data-id="element-141" data-mp="bi242rpnwk22">
              Tools &amp; logins
            </h2>
            <p className="mt-1 text-xs text-[var(--b-dim)]" data-id="element-142" data-mp="3tozqlgroy61t">
              Opens in a new tab. Shared logins are noted on the card.
            </p>
          </div>

          <div role="tablist" aria-label="Filter tools by category" className="flex flex-wrap gap-1.5" data-id="element-143" data-mp="1rvsba7c2vofx">
            {filters.map(filter => {
            const active = activeCategory === filter;
            return <button key={filter} role="tab" aria-selected={active} type="button" onClick={() => setActiveCategory(filter)} style={active ? {
              backgroundColor: 'var(--b-accent)',
              borderColor: 'var(--b-accent)',
              color: 'var(--b-on-accent)'
            } : {
              backgroundColor: 'var(--b-surface)',
              borderColor: 'var(--b-border)',
              color: 'var(--b-dim)'
            }} className="rounded-full border px-3 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--b-accent)]" data-id="element-144" data-mp="3nz6oeyd79ppu">
                  {filter}
                </button>;
          })}
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-145" data-mp="16pu0qozdk8is">
          {visibleTools.map(tool => <React.Fragment key={tool.id} data-id="element-146" data-mp="1bqhp12w7yb9b">
              <ToolCard tool={tool} data-id="element-147" data-mp="1frl1rgiyzr6b" />
              {tool.screenshot && <ScreenshotCard screenshot={tool.screenshot} data-id="element-148" data-mp="1fr1m66gslevg" />}
            </React.Fragment>)}
        </div>
      </section>

      <section aria-labelledby="docs-heading" className="mt-16" data-id="element-149" data-mp="izhj2mebuxf0">
        <h2 id="docs-heading" className="text-2xl font-bold" style={headingStyle} data-id="element-150" data-mp="o0x2yb4rqxvt">
          Shared documents
        </h2>
        <p className="mt-1 text-xs text-[var(--b-dim)]" data-id="element-151" data-mp="rzw91z28l0ou">
          Everything linked here is visible to everyone on this project.
        </p>

        <div style={{
        borderRadius: 'var(--b-radius)'
      }} className="mt-6 overflow-hidden border border-[var(--b-border)] bg-[var(--b-surface)]" data-id="element-152" data-mp="bmcpnl1olvx">
          <ul className="divide-y divide-[var(--b-border)]" data-id="element-153" data-mp="17nbvaag66ilb">
            {project.documents.map(document => <DocumentRow key={document.id} document={document} data-id="element-154" data-mp="12oqykl04mh1m" />)}
          </ul>
        </div>
      </section>
    </motion.main>;
}